/* eslint-disable */
import { useEffect, useState, useCallback, useRef } from "react";
import { useSelector } from "react-redux";
import styled from "styled-components";
import exit from "../../assets/ui/exit.svg";
import { WalletBalances } from "../../components/Web3Automation";
import { CC0_THE_CREW } from "../../config/options";
import { useBalance } from "../../contracts/views";
import { selectDelegates } from "../../state/delegateCash";
import { selectTraits } from "../../state/uwuSlice";
import { selectWarmWalletConfig } from "../../state/warmXYZ";
import {
  fetchActiveBalances,
  mergeWalletConfigs,
} from "../../utils/delegation";
import { fetchBalanceMulticall } from "../../utils/multicall";
import { UwuBalance } from "./UwuBalance";
import heart_icon from "../../assets/ui/heart.svg";

interface SelectorProps {
  show: boolean;
}

const StyledUwuSelector = styled.div.withConfig({
  shouldForwardProp: (prop) => !['show'].includes(prop),
})<SelectorProps>`
  position: fixed;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1;
  background: var(--bg);

  display: ${(props: SelectorProps) => (props.show ? "flex" : "none")};

  padding-top: 4rem;
  @media (max-width: 600px) {
    padding: 1rem;
  }
`;

const Exit = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(255, 255, 255, 0.5);
  backdrop-filter: blur(10px);
`;

const Container = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  height: 100%;
  background: var(--bg);
  padding-left: 2.4rem;
  padding-right: 2.4rem;
  padding-bottom: 2.4rem;
`;

const ExitButton = styled.button`
  position: absolute;
  top: 2.4rem;
  right: 2.4rem;
  cursor: pointer;
`;

const ExitImage = styled.img`
  width: 2rem;
`;

const Header = styled.div`
  font-size: 4.5rem;
  font-weight: 700;
  width: 100%;
`;

const Empty = styled.div`
  font-size: 2rem;
  font-weight: 500;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const SubHeader = styled.div`
  font-size: 1.8rem;
  font-weight: 700;
  color: #F1A7C8;
`;

const Icon = styled.img`
  width: 24px;
  height: 24px;
`;

const DelegateKeyContainer = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
`;

interface Props {
  address: string | null | undefined;
  show: boolean;
  close: () => void;
}

// WalletConfig or WC is refrenced in the comment for abbreviation purposes.
const UwuSelector = ({ address, show, close }: Props): JSX.Element => {
  const balance = useBalance(address);
  const traits = useSelector(selectTraits);
  const delegateCash = useSelector(selectDelegates);
  const warmXYZ = useSelector(selectWarmWalletConfig);
  const mergedWC = mergeWalletConfigs(delegateCash, warmXYZ, address);
  const [uwuWallets, setUwuWallets] = useState<WalletBalances[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const hasFetchedRef = useRef<Record<string, boolean>>({});
  const lastAddressRef = useRef<string | null>(null);
  const lastBalanceRef = useRef<number | undefined>(undefined);
  const [cachedBalance, setCachedBalance] = useState<number | undefined>(undefined);

  // Update cached balance only when it actually changes
  useEffect(() => {
    if (balance !== lastBalanceRef.current) {
      lastBalanceRef.current = balance;
      setCachedBalance(balance);
    }
  }, [balance]);

  // Memoize the fetch function to prevent unnecessary recreations
  const fetchWalletBalances = useCallback(async () => {
    if (!mergedWC.coldWallet || mergedWC.coldWallet.length === 0 || isLoading) return;

    try {
      setIsLoading(true);
      const results = await fetchBalanceMulticall(mergedWC.coldWallet);
      if (!results) return;

      // Update the fetch cache
      mergedWC.coldWallet.forEach(addr => {
        hasFetchedRef.current[addr] = true;
      });

      // Merge new results with existing wallets, ensuring no duplicates
      const mergedWB = [...uwuWallets];
      results.forEach(newWallet => {
        const existingIndex = mergedWB.findIndex(w => w.address === newWallet.address);
        if (existingIndex === -1) {
          mergedWB.push({ ...newWallet, isDelegate: true });
        } else {
          mergedWB[existingIndex] = { ...newWallet, isDelegate: true };
        }
      });

      setUwuWallets(mergedWB);
    } catch (error) {
      console.error("Error fetching wallet balances:", error);
    } finally {
      setIsLoading(false);
    }
  }, [mergedWC.coldWallet, uwuWallets, isLoading]);

  // Fetch when address changes or when warmXYZ/delegateCash changes
  useEffect(() => {
    if (address) {
      if (address !== lastAddressRef.current) {
        lastAddressRef.current = address;
        // Reset the fetch cache when address changes
        hasFetchedRef.current = {};
        setUwuWallets([]); // Clear existing wallets when address changes
        setCachedBalance(undefined); // Reset cached balance
      }
      fetchWalletBalances();
    }
  }, [address, fetchWalletBalances, warmXYZ, delegateCash]);

  // If address defined return mergedWC and connected WC
  const walletBalances: WalletBalances[] = address && cachedBalance !== undefined
    ? [
      ...uwuWallets,
      {
        address,
        balance: cachedBalance,
        isDelegate: false,
      },
    ]
    : uwuWallets;

  // Only want ActiveBalances with balances that are greater than zero
  const activeBalances = fetchActiveBalances(walletBalances);

  let cc0 = [389, 1461, 3209, 3489, 4542, 6153, 6785, 7643, 7953, 8006, 8614];
  if (address !== CC0_THE_CREW) {
    cc0 = [];
  }

  return (
    <StyledUwuSelector show={show || !traits}>
      {traits && <Exit onClick={() => close()} />}
      <Container>
        <Header>Select uwu</Header>
        {uwuWallets.length !== 0 && (
          <DelegateKeyContainer>
            <Icon src={heart_icon} alt="heart_icon" />
            <SubHeader>delegated</SubHeader>
          </DelegateKeyContainer>
        )}
        {activeBalances.length === 0 && <Empty>No uwus found in wallet</Empty>}
        {activeBalances.length !== 0 && (
          <UwuBalance active={activeBalances} close={close} cc0={cc0} />
        )}
        {traits && (
          <ExitButton onClick={() => close()}>
            <ExitImage src={exit} alt="Exit icon" />
          </ExitButton>
        )}
      </Container>
    </StyledUwuSelector>
  );
};

export default UwuSelector;
